import { CheckCircle2 } from 'lucide-react';
import type { HelpContent } from '@/data/help-content/types';
import type { RolePermissions } from '@/types/roles.types';

interface Props {
  content: HelpContent;
  permissions: RolePermissions;
  searchQuery: string;
}

function resolveDotPath(obj: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc && typeof acc === 'object') return (acc as Record<string, unknown>)[key];
    return undefined;
  }, obj);
}

function isVisible(permissions: RolePermissions, requiredPermission?: string): boolean {
  if (!requiredPermission) return true;
  return resolveDotPath(permissions as unknown as Record<string, unknown>, requiredPermission) === true;
}

function matchesSearch(text: string, query: string): boolean {
  if (!query) return true;
  return text.toLowerCase().includes(query.toLowerCase());
}

export function FunctionalityTab({ content, permissions, searchQuery }: Props) {
  const { functionality } = content;

  const visibleFeatures = functionality.keyFeatures.filter(
    (f) =>
      isVisible(permissions, f.requiredPermission) &&
      (matchesSearch(f.label, searchQuery) || matchesSearch(f.detail, searchQuery)),
  );

  const visibleSections = functionality.sectionsAndFields.filter(
    (s) =>
      isVisible(permissions, s.requiredPermission) &&
      (matchesSearch(s.name, searchQuery) || matchesSearch(s.description, searchQuery)),
  );

  const visibleUseCases = functionality.useCases.filter((u) => matchesSearch(u, searchQuery));

  const overviewMatches = matchesSearch(functionality.overview, searchQuery);

  const hasResults =
    overviewMatches || visibleFeatures.length > 0 || visibleSections.length > 0 || visibleUseCases.length > 0;

  if (!hasResults && searchQuery) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <p className="text-sm text-muted-foreground">No results found for "{searchQuery}"</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Overview */}
      {overviewMatches && (
        <div>
          <h3 className="text-sm font-semibold mb-2">Overview</h3>
          <p className="text-sm text-muted-foreground leading-relaxed">{functionality.overview}</p>
        </div>
      )}

      {/* Key Features */}
      {visibleFeatures.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold mb-3">Key Features</h3>
          <ul className="space-y-2.5">
            {visibleFeatures.map((feature) => (
              <li key={feature.label} className="flex items-start gap-2.5">
                <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5 text-success-500" />
                <div>
                  <span className="text-sm font-medium">{feature.label}</span>
                  <span className="text-sm text-muted-foreground"> — {feature.detail}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Sections & Fields */}
      {visibleSections.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold mb-3">Sections &amp; Fields</h3>
          <div className="rounded-lg border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/50 border-b border-border">
                  <th className="text-left px-3 py-2 font-semibold text-xs uppercase tracking-wide text-muted-foreground w-2/5">
                    Section / Field
                  </th>
                  <th className="text-left px-3 py-2 font-semibold text-xs uppercase tracking-wide text-muted-foreground">
                    Description
                  </th>
                </tr>
              </thead>
              <tbody>
                {visibleSections.map((section, i) => (
                  <tr
                    key={section.name}
                    className={i % 2 === 0 ? 'bg-background' : 'bg-muted/20'}
                  >
                    <td className="px-3 py-2.5 font-medium align-top">{section.name}</td>
                    <td className="px-3 py-2.5 text-muted-foreground align-top">{section.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Use Cases */}
      {visibleUseCases.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold mb-2">Use Cases</h3>
          <ul className="space-y-1.5 list-disc list-inside">
            {visibleUseCases.map((uc, i) => (
              <li key={i} className="text-sm text-muted-foreground leading-relaxed">
                {uc}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
